import React, { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Stack,
  Title,
  Text,
  Paper,
  Group,
  Badge,
  LoadingOverlay,
  Alert,
  Divider,
  ThemeIcon,
  Button,
  TextInput,
  ActionIcon,
  Tooltip,
  Breadcrumbs,
  Anchor,
  Card,
  Switch
} from '@mantine/core';
import {
  IconTag,
  IconPlus,
  IconTrash,
  IconAlertCircle,
  IconLock,
  IconArrowLeft,
  IconInfoCircle,
  IconEyeOff
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useBusinessQuery, useAddBusinessAliasMutation, useRemoveBusinessAliasMutation } from '../../../application/queries/businessQueries';
import { useAuth } from '../../hooks/useAuth';
import { CUSTOMER_ROUTES } from '../../routing/routes';

/**
 * AliasManagementPage Component
 * SESSION_220: Dedicated page for business owners to manage their aliases
 *
 * Features:
 * - Add new aliases (limited to 5 user-created)
 * - Remove user-created aliases
 * - View all aliases with source badges
 * - Keyboard support (Enter to add)
 * - Real-time validation
 *
 * Route: /my-business/aliases
 */
export const AliasManagementPage: React.FC = (): React.ReactElement => {
  const { user, role } = useAuth();
  const navigate = useNavigate();

  const [newAlias, setNewAlias] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showRejected, setShowRejected] = useState<boolean>(false);

  // Fetch business data
  const {
    data: business,
    isLoading,
    error: queryError
  } = useBusinessQuery(
    user?.businessId ?? '',
    role ?? 'BusinessOwner',
    user?.businessId ?? null,
    !!user?.businessId
  );

  const addMutation = useAddBusinessAliasMutation();
  const removeMutation = useRemoveBusinessAliasMutation();

  // SESSION_354: Calculate user alias count excluding rejected aliases
  // Rejected aliases don't count against the 5-alias limit
  const MAX_USER_ALIASES = 5;
  const userAliasCount = useMemo((): number => {
    if (!business?.aliases) return 0;
    return business.aliases.filter(a => a.source === 'user' && a.reviewStatus !== 'Rejected').length;
  }, [business?.aliases]);

  const canAddMoreAliases = userAliasCount < MAX_USER_ALIASES;

  // SESSION_354: Count rejected aliases for toggle visibility
  const rejectedAliasCount = useMemo((): number => {
    if (!business?.aliases) return 0;
    return business.aliases.filter(a => a.source === 'user' && a.reviewStatus === 'Rejected').length;
  }, [business?.aliases]);

  /**
   * Handle adding a new alias
   */
  const handleAdd = useCallback(async (): Promise<void> => {
    if (!business) {
      setError('Business data not loaded');
      return;
    }

    if (!canAddMoreAliases) {
      setError(`Maximum of ${MAX_USER_ALIASES} custom aliases allowed. Remove an existing alias to add a new one.`);
      return;
    }

    if (!newAlias.trim()) {
      setError('Alias cannot be empty');
      return;
    }

    if (newAlias.length > 200) {
      setError('Alias must be 200 characters or less');
      return;
    }

    const aliasExists = business.aliases.some(
      (a) => a.alias.toLowerCase() === newAlias.trim().toLowerCase()
    );

    if (aliasExists) {
      setError('This alias already exists');
      return;
    }

    try {
      await addMutation.mutateAsync({
        businessId: business.id,
        command: {
          businessId: business.id,
          alias: newAlias.trim()
        }
      });

      setNewAlias('');
      setError('');
    } catch (err) {
      console.error('Failed to add alias:', err);
    }
  }, [newAlias, business, addMutation, canAddMoreAliases]);

  /**
   * Handle removing an alias
   */
  const handleRemove = useCallback(
    async (aliasId: string, aliasName: string): Promise<void> => {
      if (!business) return;

      if (!window.confirm(`Are you sure you want to remove the alias "${aliasName}"?`)) {
        return;
      }

      try {
        await removeMutation.mutateAsync({
          businessId: business.id,
          aliasId
        });
      } catch (err) {
        console.error('Failed to remove alias:', err);
      }
    },
    [business, removeMutation]
  );

  /**
   * Handle Enter key press
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        void handleAdd();
      }
    },
    [handleAdd]
  );

  /**
   * Clear error when input changes
   */
  const handleInputChange = useCallback((value: string): void => {
    setNewAlias(value);
    if (error) setError('');
  }, [error]);

  /**
   * Navigate back to My Business
   */
  const handleBack = useCallback((): void => {
    navigate(CUSTOMER_ROUTES.MY_BUSINESS);
  }, [navigate]);

  // No business linked
  if (!user?.businessId) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="No Business Linked" color="yellow">
          Your account is not linked to a business yet. Please contact support.
        </Alert>
      </Container>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <LoadingOverlay visible />
      </Container>
    );
  }

  // Error state
  if (queryError || !business) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error Loading Business" color="red">
          {queryError?.message ?? 'Could not load your business information'}
        </Alert>
      </Container>
    );
  }

  // SESSION_354: Separate aliases by source and status
  // Active user aliases (non-rejected) are shown by default
  // Rejected aliases are hidden by default but can be toggled
  const activeUserAliases = business.aliases.filter(a => a.source === 'user' && a.reviewStatus !== 'Rejected');
  const rejectedUserAliases = business.aliases.filter(a => a.source === 'user' && a.reviewStatus === 'Rejected');
  const systemAliases = business.aliases.filter(a => a.source !== 'user');

  // Combine visible user aliases based on toggle
  const visibleUserAliases = showRejected
    ? [...activeUserAliases, ...rejectedUserAliases]
    : activeUserAliases;

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        {/* Breadcrumbs and Back Button */}
        <Group justify="space-between" align="center">
          <Breadcrumbs>
            <Anchor onClick={handleBack} style={{ cursor: 'pointer' }}>
              My Business
            </Anchor>
            <Text>Manage Aliases</Text>
          </Breadcrumbs>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back
          </Button>
        </Group>

        {/* Header */}
        <Paper
          p="lg"
          radius="lg"
          style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.15)'
          }}
        >
          <Group gap="lg">
            <ThemeIcon
              size={56}
              radius="xl"
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet', deg: 135 }}
            >
              <IconTag size={28} />
            </ThemeIcon>
            <Stack gap={4}>
              <Title order={2} fw={700}>
                Manage Aliases
              </Title>
              <Text c="dimmed" size="sm">
                {business.name}
              </Text>
            </Stack>
          </Group>
        </Paper>

        {/* Info Card */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Aliases help us find mentions of your business on Reddit. Add nicknames, abbreviations,
          or alternative names that people might use when talking about your business.
          Aliases are case-insensitive.
        </Alert>

        {/* Add New Alias Card */}
        <Card withBorder radius="md" padding="lg" shadow="sm">
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>Add New Alias</Title>
              <Badge
                size="md"
                variant={canAddMoreAliases ? 'light' : 'filled'}
                color={canAddMoreAliases ? 'blue' : 'red'}
              >
                {userAliasCount}/{MAX_USER_ALIASES} custom aliases
              </Badge>
            </Group>

            <Group gap="sm" align="flex-start">
              <TextInput
                placeholder="e.g., Joe's Place, The OC, Mickey D's"
                value={newAlias}
                onChange={(e): void => handleInputChange(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                style={{ flex: 1 }}
                error={error}
                maxLength={200}
                disabled={addMutation.isPending || !canAddMoreAliases}
              />
              <Button
                onClick={(): void => { void handleAdd(); }}
                leftSection={<IconPlus size={16} />}
                loading={addMutation.isPending}
                disabled={addMutation.isPending || !newAlias.trim() || !canAddMoreAliases}
              >
                Add Alias
              </Button>
            </Group>

            {!canAddMoreAliases && (
              <Text size="sm" c="red">
                Maximum custom aliases reached. Remove an existing alias to add a new one.
              </Text>
            )}
          </Stack>
        </Card>

        {/* Custom Aliases Card */}
        <Card withBorder radius="md" padding="lg" shadow="sm">
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>Your Custom Aliases</Title>
              <Group gap="md">
                {/* SESSION_354: Toggle for showing rejected aliases */}
                {rejectedAliasCount > 0 && (
                  <Tooltip label={showRejected ? 'Hide rejected aliases' : `Show ${rejectedAliasCount} rejected alias${rejectedAliasCount !== 1 ? 'es' : ''}`} withArrow>
                    <Group gap="xs">
                      <Switch
                        size="xs"
                        checked={showRejected}
                        onChange={(e): void => setShowRejected(e.currentTarget.checked)}
                        label={
                          <Group gap={4}>
                            <IconEyeOff size={14} />
                            <Text size="xs">{rejectedAliasCount} rejected</Text>
                          </Group>
                        }
                      />
                    </Group>
                  </Tooltip>
                )}
                <Badge size="sm" color="blue" variant="light">
                  {activeUserAliases.length} active alias{activeUserAliases.length !== 1 ? 'es' : ''}
                </Badge>
              </Group>
            </Group>

            {visibleUserAliases.length === 0 ? (
              <Text size="sm" c="dimmed" fs="italic">
                No custom aliases yet. Add one above to improve mention matching.
              </Text>
            ) : (
              <Stack gap="xs">
                {visibleUserAliases.map((alias) => (
                  <Group key={alias.id} justify="space-between" wrap="nowrap" p="xs" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 'var(--mantine-radius-sm)' }}>
                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                      <IconTag size={16} color="var(--mantine-color-indigo-6)" />
                      <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
                        {alias.alias}
                      </Text>
                      <Badge size="xs" variant="filled" color="blue">
                        custom
                      </Badge>
                      {alias.reviewStatus === 'Pending' && (
                        <Badge size="xs" color="yellow" variant="light">
                          Pending Review
                        </Badge>
                      )}
                      {alias.reviewStatus === 'Rejected' && (
                        <Badge size="xs" color="red" variant="light">
                          Rejected
                        </Badge>
                      )}
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={(): void => { void handleRemove(alias.id, alias.alias); }}
                      loading={removeMutation.isPending}
                      disabled={removeMutation.isPending}
                      title={`Remove alias "${alias.alias}"`}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>

        {/* System Aliases Card */}
        {systemAliases.length > 0 && (
          <Card withBorder radius="md" padding="lg" shadow="sm">
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Title order={4}>System-Generated Aliases</Title>
                  <Tooltip label="These aliases are automatically generated and cannot be removed" withArrow>
                    <ThemeIcon size="sm" variant="subtle" color="gray">
                      <IconLock size={14} />
                    </ThemeIcon>
                  </Tooltip>
                </Group>
                <Badge size="sm" color="gray" variant="light">
                  {systemAliases.length} alias{systemAliases.length !== 1 ? 'es' : ''}
                </Badge>
              </Group>

              <Text size="xs" c="dimmed">
                These aliases are automatically generated from your business data and public records.
                They help ensure comprehensive mention detection.
              </Text>

              <Divider />

              <Stack gap="xs">
                {systemAliases.map((alias) => (
                  <Group key={alias.id} justify="space-between" wrap="nowrap" p="xs" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 'var(--mantine-radius-sm)' }}>
                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                      <IconTag size={16} color="var(--mantine-color-gray-5)" />
                      <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                        {alias.alias}
                      </Text>
                      <Badge size="xs" variant="light" color="gray">
                        {alias.source || 'system'}
                      </Badge>
                    </Group>
                    <Tooltip label="System-generated aliases cannot be removed" withArrow>
                      <ActionIcon color="gray" variant="subtle" disabled>
                        <IconLock size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                ))}
              </Stack>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};
